import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { getAllMedicineList, getDoctorMedicineList } from '../../../store/slices/prescriptionSlice';
import CustomModal from '../../common/modal/modal';
import MedicineSigForm from '../main-prescription/medicine/medicine-sig-form/medicine-sig-form';
import { DraggableList } from '../../common/draggable-list/drgabble-list';
import { frequencyData } from '../../../utils/static-data/data';
import ComboInput from '../../common/updated-input/combo-input/combo-input';
import { useSigFormPreload } from '../main-prescription/medicine/use-generic-sig-suggestions';
import './medication-list-panel.scss';

const noop = () => { };

// The editable columns on a medication row.
const ROW_FIELDS = ['name', 'frequency', 'note'];

// The list + drug-picker UI shared by Past Medication (stored on the patient) and
// Current Medication (stored on the visit). This component owns only UI state; the
// caller owns `items` and supplies the four writes, because the two scopes hit
// different endpoints with different payload shapes.
const MedicationListPanel = ({
    title,
    fieldKey,
    items,
    doctorId,
    visitId,
    emptyText = 'No medications added yet',
    enableSigEdit = false,
    headerSlot = null,
    headActionsSlot = null,
    listTopSlot = null,
    onAdd,
    onDelete,
    onUpdateField,
    onReorder,
    onSigFormSaved = noop,
}) => {
    const dispatch = useDispatch();
    const { isMedicineListLoading, medicineList, doctorMedicineList, isDoctorMedicineListLoading, isLoading } =
        useSelector((state) => state.prescription);
    const { user } = useSelector((state) => state.auth);
    const language = user?.user?.details?.config?.language || 'english';
    const frequencyOptions = useMemo(
        () => (frequencyData || [])
            .map((data) => (language === 'bangla' ? data?.value_bn : data?.value_en))
            .filter((option, index, all) => option && all.indexOf(option) === index),
        [language]
    );

    const [show, setShow] = useState(false);
    const [selectedMedicine, setSelectedMedicine] = useState({});
    const [docMedicineShow, setDocMedicineShow] = useState(true);
    const [searchType, setSearchType] = useState('name');
    const [myMedicineSearchType, setMyMedicineSearchType] = useState('name');
    const [info, setInfo] = useState({});
    const [isDoubleClick, setIsDoubleClick] = useState(false);
    const [isDocMedicineUp, setIsDocMedicineUp] = useState(false);
    const [searchString, setSearchString] = useState('');
    const [ownMedicineSearchString, setOwnMedicineSearchString] = useState('');
    const [sigModalWide, setSigModalWide] = useState(false);
    const { preloadedSigs, preloadSigs } = useSigFormPreload(doctorId, language);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const timeoutIdRef = useRef(null);

    const myDrugListId = `${fieldKey}-my-drug-list`;

    useEffect(() => {
        if (!doctorId) return;
        dispatch(getDoctorMedicineList({ doctorId, search: '' }));
    }, [doctorId]);

    // Seed the row edit buffers from the server copy, but only for keys whose server
    // value actually changed. The visit document has many other writers (Chief
    // Complaint, Diagnosis, Care plan, Vitals...), and an unrelated save while the
    // doctor is mid-word would otherwise wipe the keystrokes out from under them.
    const serverSnapshotRef = useRef({});
    useEffect(() => {
        setInfo((prev) => {
            const next = { ...prev };
            const snapshot = serverSnapshotRef.current;
            (items || []).forEach((medicine) => {
                ROW_FIELDS.forEach((field) => {
                    const key = `${field}-${medicine?._id}`;
                    const serverValue = medicine?.[field] || '';
                    if (!(key in next) || snapshot[key] !== serverValue) next[key] = serverValue;
                    snapshot[key] = serverValue;
                });
            });
            return next;
        });
    }, [items]);

    const medicineSearch = (value, event) => {
        event.preventDefault();
        if (value?.length > 0 && /^\s*$/.test(value) === false) {
            let payLoad = { key: searchType, search: value.trim(), manufacturerName: '', medicinePriority: false };
            if (user?.user?.details?.sponsor === 'true') {
                payLoad.manufacturerName = user?.user?.organization?.manufacturerName;
                payLoad.medicinePriority = user?.user?.organization?.medicinePriority;
            }
            dispatch(getAllMedicineList(payLoad));
            setDocMedicineShow(false);
        } else {
            setDocMedicineShow(true);
        }
    };

    const medicineSearchWithTimeOut = (value, event) => {
        setSearchString(value);
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(() => medicineSearch(value, event), 500);
    };

    const medicineSearchFromMyList = (value, type) => {
        dispatch(getDoctorMedicineList({ doctorId, search: value?.trim(), type }));
    };

    const medicineSearchFromMyListWithTimeOut = (value, type) => {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = setTimeout(() => medicineSearchFromMyList(value, type), 500);
    };

    const checkOrganizationFun = (name = '') => {
        if (user?.user?.details?.sponsor === 'true')
            return name.startsWith(user?.user?.organization?.manufacturerName);
        return false;
    };

    // Every way into the SIG modal goes through here: the generic's saved SIGs are
    // loaded first, so the modal opens once at its final width, already filled in.
    const openSigForm = (medicine, { docMedicineUp = false, doubleClick = false } = {}) => {
        preloadSigs(medicine, (preloaded) => {
            setSelectedMedicine(medicine);
            setIsDocMedicineUp(docMedicineUp);
            setIsDoubleClick(doubleClick);
            setSigModalWide(preloaded?.presets?.length > 0);
            setShow(true);
        });
    };

    const checkAndAddMedicine = (medicine, check = false) => {
        if (medicine?.sig?.length && check) {
            addMedicine(medicine);
        } else if (check) {
            openSigForm(medicine);
        } else {
            addMedicine(medicine);
        }
        setOwnMedicineSearchString('');
    };

    const addNewMedicine = () => {
        openSigForm({});
    };

    // Field mapping lives here so both scopes store byte-identical rows.
    const addMedicine = async (medicine) => {
        setIsSubmitting(true);
        const payLoad = {
            power: medicine?.power || '',
            type: medicine?.type || '',
            generic: medicine?.generic || '',
            manufacturer: medicine?.manufacturer || '',
            medicine: medicine?.medicineId || null,
            sig: medicine?.sig || '',
            name: medicine?.name || '',
            take: medicine?.take || '',
            route: medicine?.route || '',
            frequency: medicine?.frequency || '',
            instruction: medicine?.instruction || '',
            repeat: medicine?.repeat || '',
            duration: medicine?.duration || '',
            doctorMedicineId: medicine?._id || medicine?.doctorMedicineId || '',
            addToDoctorMedicine: medicine?.addToDoctorMedicine || false,
        };
        try {
            await onAdd(payLoad);
        } finally {
            const searchInput = document.getElementById(myDrugListId);
            if (searchInput) searchInput.value = '';
            dispatch(getDoctorMedicineList({ doctorId, search: '', type: myMedicineSearchType || 'name' }));
            setIsSubmitting(false);
        }
    };

    const deleteMedicineFun = async (medicine) => {
        setIsSubmitting(true);
        try {
            await onDelete(medicine);
        } finally {
            setIsSubmitting(false);
        }
    };

    const onBlurSetInfo = (key, value) => {
        setInfo((prev) => ({ ...prev, [key]: value }));
    };

    const onBlurSubmit = (value, type, id) => {
        onUpdateField(value, type, id);
    };

    const openSigEdit = (medicine) => {
        if (!enableSigEdit) return;
        openSigForm(medicine, { doubleClick: true });
    };

    return (
        <div className={`pm-wrap${headerSlot ? ' pm-wrap--tabbed' : ''}`}>
            {headerSlot}
            <div className="pm-card">

                {/* ── List panel ── */}
                <div className="pm-list-panel">
                    <div className="pm-card-head">
                        {title}
                        {items?.length > 0 && <span className="pm-badge">{items.length}</span>}
                        {headActionsSlot && <span className="pm-head-actions">{headActionsSlot}</span>}
                    </div>
                    {listTopSlot}
                    <div className="pm-col-head">
                        <span>#</span><span>Name</span><span>Frequency</span><span>Note</span><span>Action</span>
                    </div>
                    <div className="pm-list-scroll">
                        {items?.length > 0 ? (
                            <DraggableList
                                items={items}
                                onReorder={onReorder}
                                getItemId={(m) => m?._id}
                                renderItem={(medicine, index) => (
                                    <div
                                        className="pm-row"
                                        id={`${fieldKey}-${medicine?._id}`}
                                        onDoubleClick={() => openSigEdit(medicine)}
                                    >
                                        <span className="pm-num">{index + 1}</span>
                                        {/* The fields swallow double-clicks so selecting a word
                                            doesn't open the sig form behind them. */}
                                        <input
                                            style={{ color: 'black' }}
                                            type="text"
                                            placeholder="Name"
                                            value={info[`name-${medicine?._id}`] || ''}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            onChange={(e) => onBlurSetInfo(`name-${medicine?._id}`, e.target.value)}
                                            onBlur={(e) => onBlurSubmit(e.target.value, 'name', medicine?._id)}
                                        />
                                        {/* One field: type anything, or open the chevron for the
                                            same presets the sig form offers. */}
                                        <ComboInput
                                            placeholder="Frequency"
                                            options={frequencyOptions}
                                            value={info[`frequency-${medicine?._id}`] || ''}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            onValueChange={(next) => onBlurSetInfo(`frequency-${medicine?._id}`, next)}
                                            onCommit={(next) => onBlurSubmit(next, 'frequency', medicine?._id)}
                                        />
                                        <textarea
                                            rows={2}
                                            placeholder="Note…"
                                            value={info[`note-${medicine?._id}`] || ''}
                                            onDoubleClick={(e) => e.stopPropagation()}
                                            onChange={(e) => onBlurSetInfo(`note-${medicine?._id}`, e.target.value)}
                                            onBlur={(e) => onBlurSubmit(e.target.value, 'note', medicine?._id)}
                                        />
                                        <button className="pm-del" onClick={() => deleteMedicineFun(medicine)}>
                                            <FontAwesomeIcon icon={faTrash} size="xs" />
                                        </button>
                                    </div>
                                )}
                            />
                        ) : (
                            <div className="pm-empty">{emptyText}</div>
                        )}
                    </div>
                </div>

                {/* ── Picker panel ── */}
                <div className="pm-picker-panel">
                    <div className="pm-card-head">Add Medicine</div>
                    <div className="pm-search-box">
                        <div>
                            <div className="pm-search-label">Search</div>
                            <div className="pm-search-row">
                                <input
                                    autoComplete="off"
                                    value={searchString}
                                    placeholder={`Search by ${searchType}…`}
                                    type="text"
                                    onChange={(e) => medicineSearchWithTimeOut(e.target.value, e)}
                                />
                                <select onChange={(e) => { medicineSearchWithTimeOut('', e); setSearchType(e.target.value); }}>
                                    <option value="name">Name</option>
                                    <option value="generic">Generic</option>
                                </select>
                            </div>
                        </div>
                        {docMedicineShow && (
                            <div>
                                <div className="pm-search-label">My Drug List</div>
                                <div className="pm-search-row">
                                    <input
                                        value={ownMedicineSearchString || ''}
                                        autoComplete="off"
                                        id={myDrugListId}
                                        placeholder="Filter my drugs…"
                                        type="text"
                                        onChange={(e) => {
                                            setOwnMedicineSearchString(e.target.value);
                                            medicineSearchFromMyListWithTimeOut(e.target.value, myMedicineSearchType);
                                        }}
                                    />
                                    <select onChange={(e) => {
                                        setOwnMedicineSearchString('');
                                        medicineSearchFromMyListWithTimeOut('', e.target.value);
                                        setMyMedicineSearchType(e.target.value);
                                    }}>
                                        <option value="name">Name</option>
                                        <option value="generic">Generic</option>
                                        <option value="type">Type</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="pm-picker-scroll">
                        {/* a global pick always opens the SIG form: the doctor confirms or changes the SIG there */}
                        {medicineList && !docMedicineShow && medicineList.map((medicine, index) => (
                            <div key={`pg-${index}`}
                                className={`pm-item ${checkOrganizationFun(medicine?.manufacturer) ? 'same_org' : ''}`}
                                onClick={() => checkAndAddMedicine(medicine, true)}>
                                <div style={{ color: 'black' }} className="item-type">{medicine?.type?.toLowerCase()}</div>
                                <div style={{ color: 'black' }} className="item-name">{medicine?.name}</div>
                                {medicine?.power && <div style={{ color: 'black' }} className="item-sub">{medicine.power.slice(0, 20).toLowerCase()}</div>}
                            </div>
                        ))}

                        {docMedicineShow && <div className="pm-section-tag">My Drug List</div>}
                        {docMedicineShow && doctorMedicineList?.map((medicine, index) => (
                            <div className="pm-item-row" key={`pm-${index}`}>
                                <div className="item-info" onClick={() => { if (!isSubmitting) checkAndAddMedicine(medicine); }}>
                                    <div style={{ color: 'black' }} className="item-type">{medicine?.type?.toLowerCase()}</div>
                                    <div style={{ color: 'black' }} className="item-name">{medicine?.name}</div>
                                    {medicine?.power && <div style={{ color: 'black' }} className="item-sub">{medicine.power.slice(0, 15).toLowerCase()}</div>}
                                    {(medicine?.sig || medicine?.duration) && (
                                        <div style={{ color: 'black' }} className="item-sub">{medicine?.sig}{medicine?.duration ? ' · ' + medicine.duration.replace('/', ' ') : ''}</div>
                                    )}
                                </div>
                                <button className="pm-edit-btn" onClick={() => openSigForm(medicine, { docMedicineUp: true })}>
                                    <FontAwesomeIcon icon={faEdit} size="xs" />
                                </button>
                            </div>
                        ))}

                        {(!isLoading && searchString.length > 0 && !docMedicineShow) && (
                            <div className="pm-create" onClick={addNewMedicine}>
                                + Create <span>"{searchString}"</span>
                            </div>
                        )}
                        {(isMedicineListLoading || (isDoctorMedicineListLoading && docMedicineShow)) && (
                            <div className="pm-loading">Loading…</div>
                        )}
                    </div>
                </div>

            </div>{/* end pm-card */}

            <CustomModal customClass="default" size={sigModalWide ? 'lg' : 'md'} show={show} setShow={setShow} isHeaderHide={true}>
                <MedicineSigForm
                    setPrescriptionMedicineToState={noop}
                    setPrescribedMedicines={noop}
                    visitId={visitId}
                    updateState={onSigFormSaved}
                    isDocMedicineUp={isDocMedicineUp}
                    setIsDocMedicineUp={setIsDocMedicineUp}
                    isDoubleClick={isDoubleClick}
                    setIsDoubleClick={setIsDoubleClick}
                    setDocMedicineShow={setDocMedicineShow}
                    setSearchString={setSearchString}
                    searchString={searchString}
                    doctorId={doctorId}
                    medicineAddToPrescription={addMedicine}
                    selectedMedicine={selectedMedicine}
                    setShow={setShow}
                    fieldKey={fieldKey}
                    onWideChange={setSigModalWide}
                    preloadedSigs={preloadedSigs}
                />
            </CustomModal>
        </div>
    );
};

export default MedicationListPanel;
